import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { SecondaryTabs, Switch } from "@eweadn-qiankun/shared-components";
import type { CommunityPreviewDeviceMeta } from "@eweadn-qiankun/shared-types";
import {
  DYNAMIC_GAIN_MAX,
  DYNAMIC_SPEED_MAX,
  getDisplayPoints,
  type DynamicPoint,
  type DynamicSensitivityMode,
} from "../../../../../main-app/src/sdkApi/mouse/zhanli/dynamicSensitivityCodec";
import { getMouseDynamicSensitivityPreviewValues } from "../utils/mouseConfigPreviewUtils";
import "../../../../../mouse-management/src/pages/dynamicSensitivity/index.scss";

interface Props {
  meta: CommunityPreviewDeviceMeta;
  configData?: Record<string, unknown>;
}

type ModeTabKey = "0" | "1" | "2" | "3";

const MODE_TABS: {
  value: ModeTabKey;
  mode: DynamicSensitivityMode;
  labelKey: string;
}[] = [
  { value: "0", mode: 0, labelKey: "mouse.dynamicSensitivity.modeClassic" },
  { value: "2", mode: 2, labelKey: "mouse.dynamicSensitivity.modeJump" },
  { value: "1", mode: 1, labelKey: "mouse.dynamicSensitivity.modeNatural" },
  { value: "3", mode: 3, labelKey: "mouse.dynamicSensitivity.modeCustom" },
];

const ZONES = [
  {
    from: 0,
    to: 15,
    labelKey: "mouse.dynamicSensitivity.zoneAim",
    color: "rgba(19, 194, 194, 0.15)",
  },
  {
    from: 15,
    to: 45,
    labelKey: "mouse.dynamicSensitivity.zoneDaily",
    color: "rgba(22, 119, 255, 0.15)",
  },
  {
    from: 45,
    to: 100,
    labelKey: "mouse.dynamicSensitivity.zoneFlick",
    color: "rgba(245, 34, 45, 0.15)",
  },
];

const PAD = { l: 36, r: 12, t: 40, b: 44 };

function readCssVar(name: string) {
  if (typeof window === "undefined") return "";
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

function drawCurve(
  canvas: HTMLCanvasElement,
  points: DynamicPoint[],
  zoneLabels: string[],
  axisLabels: { x: string; y: string }
) {
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  const width = Math.floor(rect.width) || 900;
  const height = Math.floor(rect.height) || 240;
  const plotW = width - PAD.l - PAD.r;
  const plotH = height - PAD.t - PAD.b;
  const toX = (speed: number) => PAD.l + (speed / DYNAMIC_SPEED_MAX) * plotW;
  const toY = (gain: number) =>
    PAD.t + plotH - (gain / DYNAMIC_GAIN_MAX) * plotH;

  canvas.width = width * dpr;
  canvas.height = height * dpr;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, width, height);

  const textMuted = readCssVar("--text-tertiary");
  const grid = readCssVar("--bg-09");
  const primary = readCssVar("--primary-color");

  ZONES.forEach((zone, i) => {
    const x = toX(zone.from);
    const w = toX(zone.to) - x;
    ctx.fillStyle = zone.color;
    ctx.fillRect(x, PAD.t, w, plotH);
    ctx.fillStyle = textMuted;
    ctx.font = "11px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(zoneLabels[i] ?? "", x + w / 2, PAD.t + 16);
  });

  ctx.strokeStyle = grid;
  ctx.lineWidth = 1;
  ctx.fillStyle = textMuted;
  ctx.font = "11px sans-serif";
  for (const xTick of [0, 25, 50, 75, 100]) {
    const x = toX(xTick);
    ctx.beginPath();
    ctx.moveTo(x, PAD.t);
    ctx.lineTo(x, PAD.t + plotH);
    ctx.stroke();
    ctx.textAlign = xTick === 0 ? "left" : xTick === 100 ? "right" : "center";
    ctx.fillText(String(xTick), x, height - 28);
  }
  for (const yTick of [0, 0.5, 1.0, 1.5, 2.0]) {
    const y = toY(yTick);
    ctx.beginPath();
    ctx.moveTo(PAD.l, y);
    ctx.lineTo(PAD.l + plotW, y);
    ctx.stroke();
    ctx.textAlign = "left";
    ctx.fillText(yTick.toFixed(1), 0, y + 4);
  }

  ctx.fillStyle = textMuted;
  ctx.font = "12px sans-serif";
  ctx.textAlign = "left";
  ctx.fillText(axisLabels.y, 0, PAD.t - 16);
  ctx.textAlign = "right";
  ctx.fillText(axisLabels.x, PAD.l + plotW, height - 8);

  ctx.strokeStyle = "rgba(0,0,0,0.1)";
  ctx.beginPath();
  ctx.rect(PAD.l, PAD.t, plotW, plotH);
  ctx.stroke();

  const sorted = [...points].sort((a, b) => a[0] - b[0]);
  ctx.strokeStyle = primary;
  ctx.lineWidth = 2;
  ctx.beginPath();
  sorted.forEach(([x, y], index) => {
    if (index === 0) ctx.moveTo(toX(x), toY(y));
    else ctx.lineTo(toX(x), toY(y));
  });
  ctx.stroke();

  sorted.forEach(([x, y]) => {
    const cx = toX(x);
    const cy = toY(y);
    ctx.fillStyle = primary;
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  });
}

/** 社区只读预览：动态灵敏（无实时速度表） */
export default function MouseDynamicSensitivitySection({
  meta: _meta,
  configData,
}: Props) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const values = useMemo(
    () => getMouseDynamicSensitivityPreviewValues(configData),
    [configData]
  );

  const activePoints = useMemo(() => {
    if (values.mode === 3) {
      return values.points.map((p) => [...p] as DynamicPoint);
    }
    return getDisplayPoints(values.mode);
  }, [values.mode, values.points]);

  const zoneLabels = useMemo(() => ZONES.map((z) => t(z.labelKey)), [t]);
  const axisLabels = useMemo(
    () => ({
      x: t("mouse.dynamicSensitivity.axisSpeed"),
      y: t("mouse.dynamicSensitivity.axisGain"),
    }),
    [t]
  );

  const redraw = useCallback(() => {
    if (!canvasRef.current) return;
    drawCurve(canvasRef.current, activePoints, zoneLabels, axisLabels);
  }, [activePoints, axisLabels, zoneLabels]);

  useEffect(() => {
    redraw();
    window.addEventListener("resize", redraw);
    return () => window.removeEventListener("resize", redraw);
  }, [redraw]);

  return (
    <div className="dynamic-sensitivity-page community-ds-preview">
      <header className="ds-header">
        <div className="ds-header__text">
          <h1>{t("mouse.dynamicSensitivity.title")}</h1>
          <p>{t("mouse.dynamicSensitivity.desc")}</p>
        </div>
        <label className="ds-enable">
          <span>{t("mouse.dynamicSensitivity.enable")}</span>
          <Switch checked={values.enable} disabled onChange={() => undefined} />
        </label>
      </header>

      <div className="ds-modes">
        <SecondaryTabs<ModeTabKey>
          activeKey={String(values.mode) as ModeTabKey}
          ariaLabel={t("mouse.dynamicSensitivity.title")}
          options={MODE_TABS.map((m) => ({
            value: m.value,
            label: t(m.labelKey),
          }))}
          onChange={() => undefined}
        />
      </div>

      <div className="ds-chart">
        <canvas
          ref={canvasRef}
          className="ds-chart__canvas"
          style={{ cursor: "default", pointerEvents: "none" }}
        />
      </div>

      <div className="ds-table-wrap">
        <div className="ds-table-grid">
          <div className="ds-table-grid__corner" />
          {activePoints.map((_, i) => (
            <div key={`h-${i}`} className="ds-table-grid__head">
              {t("mouse.dynamicSensitivity.pointN", { n: i + 1 })}
            </div>
          ))}

          <div className="ds-table-grid__label">
            {t("mouse.dynamicSensitivity.tableSpeed")}
          </div>
          {activePoints.map((p, i) => (
            <div key={`s-${i}`} className="ds-table-grid__cell">
              <input
                className="ds-input"
                disabled
                value={String(Math.round(p[0]))}
                readOnly
              />
            </div>
          ))}

          <div className="ds-table-grid__label">
            {t("mouse.dynamicSensitivity.tableGain")}
          </div>
          {activePoints.map((p, i) => (
            <div key={`g-${i}`} className="ds-table-grid__cell">
              <input
                className="ds-input"
                disabled
                value={String(p[1])}
                readOnly
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
